import { GroupBase, OptionsOrGroups } from "react-select";
import { CustomSelect } from "../atoms/dropdown";
import { SelectOption } from "./search-avaliable/SearchAvailableRoom";
import { SelectOptionValue } from "./hotel-result";
import { Search } from "lucide-react";
import {
  SearchAvailableRoom,
} from "./search-avaliable/FilterAvailableRoom";
import { useMemo } from "react";
import { iconList } from "@/assets/icons";
import { cityAndHotelFuzzyFilter } from "@/utils";
import { findOption } from "./search-avaliable/findOption";

interface SearchByCityAndHotelProps {
  listOption: SelectOption[] | { label: string; options: SelectOption[] }[];
  filterState: SearchAvailableRoom | null;
  onFilterChange: (f: SearchAvailableRoom | null) => void;
  defaultvalue?: SearchAvailableRoom | null;
  loading?: boolean;
  currentCity?: string;
  isShowClearAble?: boolean;
  isFirstCityChange?: React.MutableRefObject<boolean>;
}
/**
 * SearchByCityAndHotel Component
 *
 * Provides a searchable dropdown (city/hotel selector) integrated with fuzzy search.
 * Supports loading state, dynamic filtering, and reset behavior.
 */
const SearchByCityAndHotel = ({
  listOption,
  filterState,
  onFilterChange,
  defaultvalue,
  currentCity,
  loading = false,
  isShowClearAble = true,
  isFirstCityChange: isFirstCityChangeRef,
}: SearchByCityAndHotelProps) => {
  const { Spinner } = iconList;
  const currentSearch = filterState?.hotelOrCity?.value || "";
  /**
   * Memoized value for currently selected option.
   * Recomputes only when the list or search term changes.
   */
  const currentValue = useMemo(() => {
    if (!listOption?.length) return null;
    return findOption(listOption, currentSearch);
  }, [listOption, currentSearch]);

  /**
   * Custom loading spinner shown while data is being fetched.
   */
  const loadingComponent = () => {
    return (
      <Spinner
        className="m-auto mt-5 h-10 w-10 animate-spin text-gray-200 dark:text-gray-600"
        fill={"var(--fill-gray-stronger)"}
      />
    );
  };

  return (
    <div className="mt-10 ml-0 w-full md:ml-5">
      <CustomSelect<SelectOptionValue, false>
        inputId="city-hotel-search"
        value={currentValue}
        /**
         * Handles selection and clear actions.
         * - On selection: creates a new SearchAvailableRoom state.
         * - On clear: resets to default or null depending on current city.
         */
        onChange={(data, actionMeta) => {
          if (data) {
            const newData = {
              checkIn: null,
              checkout: null,
              rooms: [],
              hotelOrCity: {
                ...data,
              },
            };
            onFilterChange(newData);
            if (isFirstCityChangeRef && !isFirstCityChangeRef?.current) {
              isFirstCityChangeRef.current = true;
            }
          }
          if (actionMeta.action === "clear") {
            if (!!currentCity && defaultvalue) {
              onFilterChange(defaultvalue);
            } else {
              onFilterChange(null);
            }
          }
        }}
        isLoading={loading}
        loadingMessage={loadingComponent}
        options={
          listOption as OptionsOrGroups<
            SelectOptionValue,
            GroupBase<SelectOptionValue>
          >
        }
        className="!z-[9]"
        placeholder={"エリア名/ホテル名を検索"}
        /**
         * Custom fuzzy filter for matching user input with option labels.
         */
        filterOption={(option, inputValue) => {
          return cityAndHotelFuzzyFilter(
            { label: option.label, data: option.data },
            inputValue,
          );
        }}
        styles={{
          control: (base) => ({
            ...base,
            paddingLeft: "12px",
            paddingRight: "12px",
            cursor: "pointer",
          }),
          groupHeading: (base) => ({
            ...base,
            fontSize: "14px",
            fontWeight: "bold",
            color: "var(--text-gray-stronger)",
            padding: "8px 12px",
          }),
        }}
        searchIconCustom={
          <Search className="h-4 w-4 flex-shrink-0 text-gray-500 md:h-5 md:w-5" />
        }
        isClearable={isShowClearAble}
      />
    </div>
  );
};

export default SearchByCityAndHotel;
